import {
	faCheck,
	faCrosshairs,
	faFileImport,
	faPlus,
	faSortAlphaDown,
	faSync,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { DialogWrapper } from '@/components'
import { StructureObjectDto } from '@/endpoints/models'
import { useAppContext, useAuthContext } from '@/hooks'

import { AddModal } from '../../../AddModal'
import { ImportStructureModal } from '../ImportStructureModal'
import { TitleAction, TitleActions, ToggleButton } from './styles'

type HeaderActionsProps = {
	alphabet: boolean
	canReveal?: boolean
	isFullTreeLoading?: boolean
	isRevealing?: boolean
	onAlphabetChange: () => void
	onRefresh: () => void
	onRevealInTree?: () => void
	onSelectModeChange: () => void
	refreshing: boolean
	selectMode: boolean
}

export const HeaderActions = ({
	alphabet,
	canReveal,
	isFullTreeLoading,
	isRevealing,
	onAlphabetChange,
	onRefresh,
	onRevealInTree,
	onSelectModeChange,
	refreshing,
	selectMode,
}: HeaderActionsProps) => {
	const { t } = useAppContext()
	const auth = useAuthContext()

	const addDialog = (isModalOpen: boolean, onClose: () => void) =>
		isModalOpen && (
			<AddModal
				onClose={onClose}
				onRefresh={onRefresh}
				type={StructureObjectDto.TypeEnum.SYSTEM}
			/>
		)

	const importDialog = (isModalOpen: boolean, onClose: () => void) => (
		<>
			{isModalOpen && (
				<ImportStructureModal
					onClose={() => {
						onClose()
						onRefresh()
					}}
					title={t('IMPORT_SYSTEM')}
				/>
			)}
		</>
	)

	return (
		<TitleActions>
			<ToggleButton
				$isChecked={selectMode}
				onClick={onSelectModeChange}
				title={t('SELECT_MODE')}
			>
				<FontAwesomeIcon icon={faCheck} />
			</ToggleButton>

			<ToggleButton
				$isChecked={alphabet}
				onClick={onAlphabetChange}
				title={t('ALPHABETICAL_ORDER')}
			>
				<FontAwesomeIcon icon={faSortAlphaDown} />
			</ToggleButton>

			{onRevealInTree && (
				<TitleAction
					onClick={!isFullTreeLoading && canReveal && !isRevealing ? onRevealInTree : undefined}
					role="button"
					style={{
						opacity: canReveal && !isRevealing && !isFullTreeLoading ? 1 : 0.4,
						cursor: canReveal && !isRevealing && !isFullTreeLoading ? 'pointer' : 'not-allowed',
					}}
					title={t('REVEAL_IN_TREE')}
				>
					<FontAwesomeIcon icon={faCrosshairs} spin={isRevealing} />
				</TitleAction>
			)}

			<TitleAction
				aria-busy={refreshing || isFullTreeLoading}
				data-testid="tree-panel-refresh"
				onClick={refreshing || isFullTreeLoading ? undefined : onRefresh}
				role="button"
				title={t('REFRESH')}
			>
				<FontAwesomeIcon icon={faSync} spin={refreshing || isFullTreeLoading} />
			</TitleAction>

			{auth.canCreateSystem() && (
				<>
					<DialogWrapper dialog={addDialog}>
						{(onClick) => (
							<TitleAction
								data-testid="tree-panel-create-system"
								onClick={onClick}
								role="button"
								title={t('CREATE_SYSTEM')}
							>
								<FontAwesomeIcon icon={faPlus} />
							</TitleAction>
						)}
					</DialogWrapper>
					<DialogWrapper dialog={importDialog}>
						{(onClick) => (
							<TitleAction
								data-testid="tree-panel-import-system"
								onClick={onClick}
								role="button"
								title={t('IMPORT_SYSTEM')}
							>
								<FontAwesomeIcon icon={faFileImport} />
							</TitleAction>
						)}
					</DialogWrapper>
				</>
			)}
		</TitleActions>
	)
}

